
export const getClaimStatus = (claim) => {
  const status = claim.claimStatus ?? claim.status ?? claim.claim_status ?? '';
  return status === null || status === undefined ? '' : String(status).trim();
};

export const parseClaimDate = (dateValue) => {
  if (dateValue === null || dateValue === undefined || dateValue === '') {
    return null;
  }

  const raw = String(dateValue);

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const dotNetMatch = raw.match(/\/Date\((\d+)\)\//);
  if (dotNetMatch) {
    return new Date(Number(dotNetMatch[1]));
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatClaimDateForDisplay = (dateValue) => {
  if (!dateValue) {
    return '-';
  }

  const date = parseClaimDate(dateValue);
  if (!date) {
    return String(dateValue);
  }

  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const buildDateSearchParts = (dateValue) => {
  if (dateValue === null || dateValue === undefined || dateValue === '') {
    return [];
  }

  const parts = new Set([String(dateValue)]);
  const date = parseClaimDate(dateValue);

  if (date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = String(date.getFullYear());

    parts.add(formatClaimDateForDisplay(dateValue));
    parts.add(`${mm}/${dd}/${yyyy}`);
    parts.add(`${yyyy}-${mm}-${dd}`);
    parts.add(`${mm}-${dd}-${yyyy}`);
    parts.add(`${mm}/${dd}`);
  }

  return Array.from(parts).map((part) => part.toLowerCase());
};

export const formatBilledAmountForDisplay = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }

  const raw = String(amount);
  if (raw.includes('$')) {
    return raw;
  }

  const numericValue = Number(raw.replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(numericValue)) {
    return raw;
  }

  return numericValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const billedAmountSearchParts = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return [];
  }

  const parts = new Set([String(amount).toLowerCase()]);
  const display = formatBilledAmountForDisplay(amount);
  if (display && display !== '-') {
    parts.add(display.toLowerCase());
    parts.add(display.replace(/[$,]/g, '').toLowerCase());
  }

  const numericValue = Number(String(amount).replace(/[^0-9.-]/g, ''));
  if (!Number.isNaN(numericValue)) {
    parts.add(String(numericValue));
    parts.add(numericValue.toFixed(2));
  }

  return Array.from(parts);
};

const claimMatchesSearchTerm = (claim, term) => {
  const textFields = [
    claim.claimNumber,
    claim.facilityName,
    claim.practitionerName,
    claim.planName,
  ];

  if (textFields.some((field) => field && field.toString().toLowerCase().includes(term))) {
    return true;
  }

  const dateSearchParts = [
    ...(claim.claimDateSearchParts || []),
    ...(claim.processedDateSearchParts || []),
  ];
  if (dateSearchParts.some((part) => part.includes(term))) {
    return true;
  }

  return (claim.billedAmountSearchParts || []).some((part) => part.includes(term));
};

export const enrichClaim = (claim) => {
  const claimStatus = getClaimStatus(claim);

  return {
    ...claim,
    claimStatus,
    claimDateDisplay: formatClaimDateForDisplay(claim.claimDate),
    claimDateSearchParts: buildDateSearchParts(claim.claimDate),
    processedDateDisplay: formatClaimDateForDisplay(claim.processedDate),
    processedDateSearchParts: buildDateSearchParts(claim.processedDate),
    billedAmountDisplay: formatBilledAmountForDisplay(claim.billedAmount),
    billedAmountSearchParts: billedAmountSearchParts(claim.billedAmount),
    systemLabel: claim.isHrp ? 'HealthRules Payer' : 'Javelina',
    providerDisplay: [claim.facilityName, claim.practitionerName].filter(Boolean).join(' — '),
  };
};

export const filterClaims = (claims, { searchText, claimType, system, status }) => {
  let filtered = claims;

  if (searchText && searchText.trim()) {
    const term = searchText.trim().toLowerCase();
    filtered = filtered.filter((claim) => claimMatchesSearchTerm(claim, term));
  }
  if (claimType && claimType !== 'ALL') {
    filtered = filtered.filter((claim) =>
      (claim.claimType ?? '').toString().toLowerCase() === claimType.toLowerCase()
    );
  }

  if (system && system !== 'ALL') {
    filtered = filtered.filter((claim) => {
      if (system === 'HRP') return claim.isHrp === true;
      if (system === 'JV') return claim.isHrp === false;
      return true;
    });
  }

  if (status && status !== 'ALL') {
    filtered = filtered.filter((claim) =>
      getClaimStatus(claim).toString().toLowerCase() === status.toLowerCase()
    );
  }

  return filtered;
};

export const getDistinctValues = (claims, key) => {
  const values = claims.map((claim) => {
    if (key === 'claimStatus') {
      return getClaimStatus(claim);
    }
    return claim[key];
  }).filter((value) => value !== undefined && value !== null && value !== '');
  return [...new Set(values)].sort();
};
export const formatAmountForDisplay = (amount) => formatBilledAmountForDisplay(amount);

export const buildClaimDetailDisplay = (detail, selectedClaim) => {
  if (!detail) return {};

  const isHrp = detail.isHrp ?? false;
  const formatDateField = (value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    const formatted = formatClaimDateForDisplay(value);
    return formatted === '-' ? String(value) : formatted;
  };

  const display = {
    'Claim #': detail.claimId ?? '-',
    'Member ID': detail.memberId ?? '-',
    'Group ID': detail.groupId ?? '-',
    'Type': detail.type ?? '-',
    'Date of Service': formatDateField(detail.claimDate),
    'Received Date': formatDateField(detail.receivedDate),
    'Processed Date': formatDateField(detail.processedDate),
    'Billed Amount': formatAmountForDisplay(detail.billedAmount),
    'Allowed Amount': formatAmountForDisplay(detail.allowedAmount),
    'Deductible': formatAmountForDisplay(detail.deductible),
    'Copay': formatAmountForDisplay(detail.copay),
    'Total Member Share': formatAmountForDisplay(detail.totalOutOfPocket),
    'Plan Paid': formatAmountForDisplay(detail.planPaid),
    'Payment Date': formatDateField(detail.paymentDate),
  };

  if (!isHrp) {
    display['Payment Number'] = detail.paymentNumber ?? '-';
    display['Payment Method'] = detail.paymentMethod ?? '-';
    display['Plan Year'] = detail.planYear ?? selectedClaim?.planYear ?? '-';
    display['Benefit Code'] = detail.benefitCode ?? selectedClaim?.benefitCode ?? '-';
  }

  display['System'] = isHrp ? 'HRP' : 'Javelina';
  display['Plan Name'] = detail.planName ?? selectedClaim?.planName ?? '-';

  return display;
};

export const parseClaimsListResponse = (data) => {
  if (!data) return [];

  if (typeof data === 'string') {
    try {
      return parseClaimsListResponse(JSON.parse(data));
    } catch {
      return [];
    }
  }

  if (Array.isArray(data.claimSummaryList)) {
    return data.claimSummaryList;
  }

  if (data.resultObject && Array.isArray(data.resultObject.claimSummaryList)) {
    return data.resultObject.claimSummaryList;
  }

  return [];
};

export const parseClaimDetailResponse = (data) => {
  if (!data) return null;

  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (data.resultObject) {
    return typeof data.resultObject === 'string'
      ? JSON.parse(data.resultObject)
      : data.resultObject;
  }

  return data;
};
